import {
  formatTimeByMode,
  TimeDisplayMode,
} from "@/shared/components/visualization/WorkspaceCtx";

import {
  isSeekTargetInBounds,
  parseToSeekTarget,
  type SeekTarget,
} from "./seekParsers";
import styles from "./TimeDisplayField.module.css";
import { TimeInput } from "./TimeInput";
import { TimeModePicker } from "./TimeModePicker";

// Each hint mirrors the shape the corresponding formatter emits, so a user
// who copies the displayed value back into the input sees a hint that matches
// what they pasted. The parsers accept some leniency around this shape, but
// the hint should reflect the canonical round-trip format.
const PARSE_ERROR_HINTS: Record<TimeDisplayMode, string> = {
  [TimeDisplayMode.Elapsed]: "Expected seconds (e.g. 12.345)",
  [TimeDisplayMode.ElapsedTime]: "Expected HH:MM:SS.nnnnnnnnn",
  [TimeDisplayMode.Log]: "Expected epoch seconds (e.g. 1710524445.123)",
  [TimeDisplayMode.Local]: "Expected YYYY-MM-DD HH:MM:SS.mmm AM/PM TZ",
  [TimeDisplayMode.UTC]: "Expected YYYY-MM-DDTHH:MM:SS.nnnnnnnnnZ",
};

interface TimeDisplayFieldProps {
  mode: TimeDisplayMode;
  currentTimeNs: bigint;
  earliestTimeNs: bigint | null;
  latestTimeNs: bigint | null;
  durationMs: number;
  onModeChange: (mode: TimeDisplayMode) => void;
  onSeek: (target: SeekTarget) => void;
}

export function TimeDisplayField({
  mode,
  currentTimeNs,
  earliestTimeNs,
  latestTimeNs,
  durationMs,
  onModeChange,
  onSeek,
}: TimeDisplayFieldProps) {
  const formattedCurrent = formatTimeByMode(
    currentTimeNs,
    mode,
    earliestTimeNs,
  );

  // Returns null on a successful seek OR a no-op (raw matches the live value);
  // returns an explanatory hint string when the input can't be parsed or
  // resolves outside the loaded range. TimeInput uses null vs. non-null to
  // drive both the red styling and the tooltip copy.
  const handleSubmit = (raw: string): string | null => {
    if (raw === formattedCurrent) {
      return null;
    }
    const target = parseToSeekTarget(raw, mode);
    if (target === null) {
      return PARSE_ERROR_HINTS[mode];
    }
    if (
      !isSeekTargetInBounds(target, {
        earliestTimeNs,
        latestTimeNs,
        durationMs,
      })
    ) {
      return "Outside loaded data range";
    }
    onSeek(target);
    return null;
  };

  return (
    <div className={styles.container}>
      <TimeModePicker
        mode={mode}
        currentTimeNs={currentTimeNs}
        earliestTimeNs={earliestTimeNs}
        onModeChange={onModeChange}
      />
      <div className={styles.divider} />
      <TimeInput formatted={formattedCurrent} onSubmit={handleSubmit} />
    </div>
  );
}
